import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Header,
  Link,
  PropertyFilter,
  PropertyFilterProps,
  SegmentedControl,
  SpaceBetween,
  Table,
  Toggle,
} from "@amzn/awsui-components-react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import useCurrentUser from "../../hooks/useCurrentUser";
import useFittedColumns from "../../hooks/useFittedColumns";
import {
  InvestigationActivityCase,
  InvestigationEvent,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  ActivityScope,
  useInvestigationActivity,
} from "../../hooks/useInvestigations";
import {
  DetailText,
  commentAttachments,
  commentText,
  eventDetail,
  eventLabel,
} from "./InvestigationTimeline";
import { InlineAttachmentProvider } from "./attachments/inlineAttachments";
import {
  STATUS_LABELS,
  formatTimestamp,
  investigationPath,
  propertyFilterI18n,
} from "./investigationUtils";
import { OneLine } from "../common/OneLine";

interface InvestigationActivityFeedProps {
  stage: Stage;
}

const SCOPE_PARAM = "scope";
export const HIDE_MINE_STORAGE_KEY = "lusca.activity.hideMine";

const readHideMine = (): boolean => {
  try {
    return window.localStorage.getItem(HIDE_MINE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writeHideMine = (value: boolean) => {
  try {
    window.localStorage.setItem(HIDE_MINE_STORAGE_KEY, String(value));
  } catch {
    // Storage unavailable (private mode): the toggle still works for this visit.
  }
};

export const parseActivityScope = (value: string | null): ActivityScope =>
  value === "mine" ? "mine" : "all";

/** Detail of a feed row: a deleted comment says so rather than showing an empty cell. */
export function activityDetail(event: InvestigationEvent): string {
  if (event.type === "COMMENT" && event.deletedAt) return "(comment deleted)";
  if (event.type === "COMMENT") return commentText(event);
  return eventDetail(event);
}

/** Coarse grouping of event types, so the filter can say "Kind = Comments" in one token. */
export type ActivityKind =
  | "Comments"
  | "Status changes"
  | "Ownership"
  | "Redrives"
  | "Population"
  | "Links and tasks"
  | "Other";

export const ACTIVITY_KINDS: ActivityKind[] = [
  "Comments",
  "Status changes",
  "Ownership",
  "Redrives",
  "Population",
  "Links and tasks",
  "Other",
];

export function activityKind(type: string | undefined): ActivityKind {
  const t = type || "";
  if (t.startsWith("COMMENT")) return "Comments";
  if (["STATUS_CHANGED", "REOPENED", "DUPLICATE_CLOSED"].includes(t))
    return "Status changes";
  if (
    t === "CLAIMED" ||
    t.startsWith("OWNERSHIP_") ||
    t === "CONTRIBUTOR_ADDED" ||
    t === "CONTRIBUTOR_REMOVED" ||
    t === "FOLLOWED" ||
    t === "UNFOLLOWED"
  )
    return "Ownership";
  if (
    t.startsWith("REDRIVE_") ||
    t.startsWith("DOCUMENT_REDRIVE_") ||
    t.startsWith("DELETION_") ||
    t === "MONITORING_CLEAR"
  )
    return "Redrives";
  if (
    t.startsWith("POPULATION_") ||
    t.startsWith("ITEMS_") ||
    t.startsWith("MERGE") ||
    t === "CHILD_CREATED" ||
    t === "SPLIT_BY_LIST" ||
    t === "CREATED_FROM_LIST"
  )
    return "Population";
  if (
    t.startsWith("LINK_") ||
    t.startsWith("TASK_") ||
    t.startsWith("ARTIFACT_")
  )
    return "Links and tasks";
  return "Other";
}

/** Flat, filterable projection of a feed event. */
interface ActivityRow {
  eventId: string;
  kind: ActivityKind;
  what: string;
  who: string;
  investigation: string;
  detail: string;
  event: InvestigationEvent;
}

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    key: "kind",
    propertyLabel: "Kind",
    groupValuesLabel: "Kinds",
    operators: ["=", "!="],
  },
  {
    key: "what",
    propertyLabel: "What",
    groupValuesLabel: "Events",
    operators: ["=", "!="],
  },
  {
    key: "who",
    propertyLabel: "Who",
    groupValuesLabel: "People",
    operators: ["=", "!="],
  },
  {
    key: "investigation",
    propertyLabel: "Investigation",
    groupValuesLabel: "Investigations",
    operators: ["=", "!=", ":", "!:"],
  },
  {
    key: "detail",
    propertyLabel: "Detail",
    groupValuesLabel: "Details",
    operators: [":", "!:"],
  },
];

/** The feed opens on comments only; the filter bar can widen it. */
export const DEFAULT_ACTIVITY_QUERY: PropertyFilterProps.Query = {
  operation: "and",
  tokens: [{ propertyKey: "kind", operator: "=", value: "Comments" }],
};

const EMPTY_QUERY: PropertyFilterProps.Query = { operation: "and", tokens: [] };

const isHideMineToken = (t: PropertyFilterProps.Token, user: string): boolean =>
  t.propertyKey === "who" && t.operator === "!=" && t.value === user;

export const hasHideMine = (
  query: PropertyFilterProps.Query,
  user: string
): boolean => query.tokens.some((t) => isHideMineToken(t, user));

/** `query` with the `Who != user` token added or removed. */
export function withHideMine(
  query: PropertyFilterProps.Query,
  user: string | null | undefined,
  on: boolean
): PropertyFilterProps.Query {
  if (!user) return query;
  const rest = query.tokens.filter((t) => !isHideMineToken(t, user));
  return {
    ...query,
    tokens: on
      ? [...rest, { propertyKey: "who", operator: "!=", value: user }]
      : rest,
  };
}

const PROPERTY_FILTER_I18N = propertyFilterI18n(
  "Filter activity",
  "Filter by kind, who, investigation…"
);

const renderTime = (event: InvestigationEvent) => (
  <Box color="text-body-secondary" fontSize="body-s">
    <OneLine>{formatTimestamp(event.timestamp)}</OneLine>
  </Box>
);

const renderWho = (r: { who: string }) => <OneLine>{r.who}</OneLine>;

const renderWhat = (event: InvestigationEvent) => (
  <OneLine title={eventLabel(event)}>
    <strong>{eventLabel(event)}</strong>
  </OneLine>
);

const renderDetail = (event: InvestigationEvent, stage: Stage) => (
  <InlineAttachmentProvider
    stage={stage}
    investigationId={event.investigationId}
    attachments={
      event.type === "COMMENT" ? commentAttachments(event) : undefined
    }
  >
    <DetailText
      text={activityDetail(event)}
      markdown={event.type === "COMMENT" && !event.deletedAt}
    />
  </InlineAttachmentProvider>
);

const EMPTY_TEXT: Record<ActivityScope, string> = {
  all: "No activity yet.",
  mine: "Nothing on your investigations yet.",
};

/**
 * Stage-wide feed of what people did on investigations (comments, status changes, claims,
 * redrives, merges…), newest first. System and worker events are left out by the API. The
 * All / My cases toggle is `?scope=` so a filtered feed is a plain link.
 */
export const InvestigationActivityFeed: React.FC<
  InvestigationActivityFeedProps
> = ({ stage }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = parseActivityScope(searchParams.get(SCOPE_PARAM));
  const activity = useInvestigationActivity(stage, scope);
  const currentUser = useCurrentUser();
  const { events } = activity;

  const setScope = (next: ActivityScope) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete(SCOPE_PARAM);
    else params.set(SCOPE_PARAM, next);
    setSearchParams(params, { replace: true });
  };

  const caseOf = (event: InvestigationEvent) =>
    activity.investigations[event.investigationId || ""] as
      | InvestigationActivityCase
      | undefined;

  const rows: ActivityRow[] = events.map((event) => ({
    eventId: event.eventId || "",
    kind: activityKind(event.type),
    what: eventLabel(event),
    who: event.actor || "",
    investigation: caseOf(event)?.title || event.investigationId || "",
    detail: activityDetail(event),
    event,
  }));
  const distinct = (pick: (r: ActivityRow) => string) =>
    Array.from(new Set(rows.map(pick).filter(Boolean))).sort();
  const filteringOptions: PropertyFilterProps.FilteringOption[] = [
    ...ACTIVITY_KINDS.map((value) => ({ propertyKey: "kind", value })),
    ...distinct((r) => r.what).map((value) => ({ propertyKey: "what", value })),
    ...distinct((r) => r.who).map((value) => ({ propertyKey: "who", value })),
    ...distinct((r) => r.investigation).map((value) => ({
      propertyKey: "investigation",
      value,
    })),
  ];
  const {
    items: shownRows,
    filteredItemsCount,
    actions,
    propertyFilterProps,
  } = useCollection(rows, {
    propertyFiltering: {
      filteringProperties: FILTERING_PROPERTIES,
      filteringOptions,
      defaultQuery: withHideMine(
        DEFAULT_ACTIVITY_QUERY,
        currentUser,
        readHideMine()
      ),
    },
  });
  const { query } = propertyFilterProps;
  const filtered = query.tokens.length > 0;
  // The toggle is just a shortcut for the visible `Who != me` token, so the two never disagree.
  const hideMine = !!currentUser && hasHideMine(query, currentUser);
  const setHideMine = (value: boolean) => {
    writeHideMine(value);
    actions.setPropertyFiltering(withHideMine(query, currentUser, value));
  };
  // Remember the choice when the token is removed/added from the filter bar too.
  React.useEffect(() => {
    if (currentUser) writeHideMine(hideMine);
  }, [currentUser, hideMine]);
  // The user can resolve after the first render: add the remembered token then.
  const initialHideMine = React.useRef(readHideMine());
  const userApplied = React.useRef(false);
  React.useEffect(() => {
    if (!currentUser || userApplied.current) return;
    userApplied.current = true;
    if (initialHideMine.current && !hasHideMine(query, currentUser))
      actions.setPropertyFiltering(withHideMine(query, currentUser, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  const renderCase = (event: InvestigationEvent) => {
    const id = event.investigationId || "";
    const summary = caseOf(event);
    const href = investigationPath(id, stage);
    return (
      <SpaceBetween size="xxxs">
        <Link
          href={href}
          onFollow={(e) => {
            e.preventDefault();
            navigate(href);
          }}
        >
          {summary?.title || id}
        </Link>
        {summary?.status && (
          <Box color="text-body-secondary" fontSize="body-s">
            {STATUS_LABELS[summary.status] || summary.status}
            {summary.owner ? ` · ${summary.owner}` : " · no owner"}
          </Box>
        )}
      </SpaceBetween>
    );
  };

  // Investigation takes a share of the width (wide on a desktop, narrower on a laptop) and
  // Detail the rest; drag a header edge to resize (kept per browser).
  const fittedTable = useFittedColumns<ActivityRow>(
    "lusca.investigations.activity.columnWidths.v1",
    [
      {
        id: "time",
        header: "Time",
        width: 170,
        cell: (r) => renderTime(r.event),
      },
      {
        id: "actor",
        header: "Who",
        width: 120,
        cell: renderWho,
      },
      {
        id: "event",
        header: "What",
        width: 160,
        cell: (r) => renderWhat(r.event),
      },
      {
        id: "investigation",
        header: "Investigation",
        fit: { share: 0.34, min: 240, max: 520 },
        minWidth: 180,
        cell: (r) => renderCase(r.event),
      },
      {
        id: "detail",
        header: "Detail",
        fill: true,
        minWidth: 260,
        cell: (r) => renderDetail(r.event, stage),
      },
    ]
  );

  const errorText = activity.error?.message;
  let emptyText: React.ReactNode = EMPTY_TEXT[scope];
  if (errorText) emptyText = `Could not load activity: ${errorText}`;
  else if (rows.length > 0 && shownRows.length === 0)
    emptyText = (
      <SpaceBetween size="xs" alignItems="center">
        <span>
          Nothing matches the current filters
          {activity.hasNextPage ? " in the loaded activity" : ""}.
        </span>
        <Button onClick={() => actions.setPropertyFiltering(EMPTY_QUERY)}>
          Clear filters
        </Button>
      </SpaceBetween>
    );

  const more = activity.hasNextPage ? "+" : "";
  let counter: string | undefined;
  if (activity.isSuccess)
    counter = filtered
      ? `(${filteredItemsCount ?? 0}/${rows.length}${more})`
      : `(${rows.length}${more})`;

  return (
    <div ref={fittedTable.containerRef}>
      <Table
        key={fittedTable.tableKey}
        header={
          <Header
            variant="h2"
            counter={counter}
            actions={
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <Toggle
                  checked={hideMine}
                  disabled={!currentUser}
                  onChange={({ detail }) => setHideMine(detail.checked)}
                >
                  Hide my activity
                </Toggle>
                <SegmentedControl
                  label="Whose activity"
                  selectedId={scope}
                  onChange={({ detail }) =>
                    setScope(detail.selectedId as ActivityScope)
                  }
                  options={[
                    { id: "all", text: "Everyone" },
                    { id: "mine", text: "My investigations" },
                  ]}
                />
                <Button
                  iconName="refresh"
                  ariaLabel="Refresh activity"
                  loading={
                    activity.isFetching &&
                    !activity.isFetchingNextPage &&
                    !activity.isLoading
                  }
                  onClick={() => {
                    activity.refetch().catch(console.error);
                  }}
                />
              </SpaceBetween>
            }
            description={
              scope === "mine"
                ? "Cases you own or contribute to, and anything you did."
                : "What people did on every investigation in this stage."
            }
          >
            Recent activity
          </Header>
        }
        items={shownRows}
        trackBy="eventId"
        loading={activity.isLoading}
        loadingText="Loading activity"
        wrapLines
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            i18nStrings={PROPERTY_FILTER_I18N}
            countText={
              filtered
                ? `${filteredItemsCount ?? 0} match${
                    filteredItemsCount === 1 ? "" : "es"
                  }`
                : ""
            }
            expandToViewport
          />
        }
        {...fittedTable.tableProps}
        empty={
          <Box textAlign="center" color="inherit">
            {emptyText}
          </Box>
        }
        footer={
          activity.hasNextPage ? (
            <Box textAlign="center">
              <Button
                loading={activity.isFetchingNextPage}
                onClick={() => {
                  activity.fetchNextPage().catch(console.error);
                }}
              >
                Load older activity
              </Button>
            </Box>
          ) : undefined
        }
      />
    </div>
  );
};

export default InvestigationActivityFeed;
